import { Select, Textarea, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { type WarehouseZoneFormSchema, WarehouseZoneTypes } from "@msuite/utils/schemas";
import { useFormContext } from "react-hook-form";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useWarehouseZones } from "@/hooks/use-warehouse-zones";

/** Props Interface */
interface WarehouseZoneFormProps {
	/**
	 * The zone being edited, absent while one is being created.
	 *
	 * Only the follow-up picker reads it, and only to keep the zone from being offered as its own
	 * next stop. The database refuses that anyway — and refuses the longer loops this cannot see,
	 * where A points at B and B is edited to point back — so this is a courtesy to the person
	 * filling the form, not the guard.
	 */
	warehouseZoneId?: string;
}

export function WarehouseZoneForm({ warehouseZoneId }: WarehouseZoneFormProps) {
	/** Context */
	const methods = useFormContext<WarehouseZoneFormSchema>();
	const mentionOptions = useMentionOptions();

	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { warehouseZones } = useWarehouseZones();

	/** Computed */
	const followUpOptions = warehouseZones
		.filter((warehouseZone) => warehouseZone.id !== warehouseZoneId)
		.map((warehouseZone) => ({
			label: warehouseZone.name,
			value: warehouseZone.id,
			item: warehouseZone,
		}));

	return (
		<div className="v-stack gap-4">
			<TextInput
				isRequired
				label={t("general.name")}
				control={methods.control}
				name="name"
				placeholder={t("placeholders.stock.warehouse_zone_name")}
			/>
			<Select
				control={methods.control}
				name="type"
				isRequired
				label={t("stock.warehouse_zone_type.warehouse_zone_type")}
				isNullable={false}
				options={WarehouseZoneTypes.map((type) => ({
					label: t(`stock.warehouse_zone_type.${type}`),
					value: type,
					item: { type },
				}))}
			/>
			<Select
				control={methods.control}
				name="followUpZoneId"
				isRequired={false}
				label={t("stock.follow_up_zone")}
				helperText={t("stock.follow_up_zone_description")}
				options={followUpOptions}
			/>
			<Textarea
				isRequired={false}
				label={t("general.description")}
				control={methods.control}
				name="description"
				disableFormatting
				{...mentionOptions}
			/>
		</div>
	);
}
